/**
 * Mixin for add appointment list
 * Author: Tirthamouli Baidya
 */

import { mapMutations } from "vuex";
import { providerDependentMixin } from "./providerDependentMixin";

/**
 * Common function to get the list
 * @param {*} selectedProviderId
 * @param {*} appointmentType
 * @param {*} listName
 */
function commonGetList(selectedProviderId, appointmentType, listName) {
  // Step 1: Check if there is a selected provider id and there is any clients for the selected provider
  if (
    selectedProviderId === false ||
    !Object.prototype.hasOwnProperty.call(
      appointmentType[listName],
      selectedProviderId
    )
  ) {
    return [];
  }

  // Step 2: Return the client
  return appointmentType[listName][selectedProviderId].filter(client => {
    return client.status !== 2 && client.status !== "2";
  });
}

/**
 * Escape the following
 * @param {String} s
 */
function escape(s) {
  // eslint-disable-next-line no-useless-escape
  return s.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
}

/**
 * Filter the given array of users including filter for search term
 * @param {Object} user
 */
function filter(user) {
  return (
    user.name
      .replace(/&amp;/gi, "&")
      .replace(/&amp;/gi, "&")
      .toLowerCase()
      .search(escape(this.term.toLowerCase())) !== -1
  );
}

/**
 * Remove &amp; from array of users
 * @param {Object} user
 */
function fullMap(user) {
  return {
    ...user,
    name: user.name.replace(/&amp;/gi, "&").replace(/&amp;/gi, "&")
  };
}

export const listMixin = {
  mixins: [providerDependentMixin],
  computed: {
    /**
     * Clients from the add appointment
     */
    clients() {
      return commonGetList(
        this.selectedProviderId,
        this.getAppointmentType,
        "clients"
      );
    },
    /**
     * Get the person list
     */
    people() {
      return commonGetList(
        this.selectedProviderId,
        this.getAppointmentType,
        "people"
      );
    },
    /**
     * Get the events list
     */
    events() {
      return commonGetList(
        this.selectedProviderId,
        this.getAppointmentType,
        "events"
      );
    },
    /**
     * Gets the group list
     */
    groups() {
      return commonGetList(
        this.selectedProviderId,
        this.getAppointmentType,
        "groups"
      );
    },
    /**
     * Full result without filter
     */
    fullResult() {
      // Step 1: Full list
      const clients = this.clients.map(fullMap.bind(this));
      const people = this.people.map(fullMap.bind(this));
      const events = this.events.map(fullMap.bind(this));
      const groups = this.groups.map(fullMap.bind(this));
      return [...clients, ...people, ...events, ...groups].sort((a, b) => {
        return a.name > b.name ? 1 : -1;
      });
    },
    /**
     * Gets the total result list
     */
    results() {
      // Step 1: Filter clients
      const clients = this.clients.filter(filter.bind(this));
      const people = this.people.filter(filter.bind(this));
      const events = this.events.filter(filter.bind(this));
      const groups = this.groups.filter(filter.bind(this));
      return [...clients, ...people, ...events, ...groups]
        .map(val => {
          return {
            ...val,
            name: val.name.replace(/&amp;/gi, "&").replace(/&amp;/gi, "&")
          };
        })
        .sort((a, b) => {
          return a.name > b.name ? 1 : -1;
        })
        .splice(0, 10);
    }
  },
  methods: {
    ...mapMutations({
      setAppointmentType: "setAppointmentType"
    }),
    /**
     * Handle when one option is selected
     */
    selectOption({ type, id }) {
      this.setAppointmentType({
        selectedId: id,
        selectedType: type
      });
      if (this.hideResult) {
        this.hideResult();
      }
      if (this.hideFullResultList) {
        this.hideFullResultList();
      }
    }
  }
};
